/**
 * TransferOwnershipDialog (Doc 26 §2.3 Commit 5)
 *
 * Diálogo dedicado a transferir formalmente el liderazgo del proyecto
 * al nuevo PM. Reemplaza el patrón "agregar otro PM y que el más
 * reciente updated_at gane" — explícito, auditable, intencional.
 *
 * Reglas que aplica el backend (`POST /projects/{id}/transfer-ownership`):
 *  · El target member debe pertenecer al proyecto (409 LEAD_MEMBER_NOT_FOUND).
 *  · Su role debe tener `is_project_lead=true` en el catálogo
 *    (409 LEAD_MEMBER_NOT_LEAD_ROLE). El diálogo SOLO ofrece members
 *    cuyo role es lead para evitar este error desde la UI.
 *  · Idempotente: si el target ya es lead, no-op silencioso (el dialog
 *    desactiva el botón cuando target === currentLead para feedback claro).
 *
 * Audit event: `project.ownership_transferred` con el payload
 * `{previous_lead_member_id, new_lead_member_id, new_lead_user_id,
 * new_lead_role, reason}`. La razón es opcional pero recomendada.
 */

import { useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { ApiError } from '../../api/client'
import type { ProjectMember } from '../../api/project_members'
import { roleLabel, type RoleOption } from './team/roles'
import { transferOwnership } from '../../api/projects'
import { useAccessToken } from '../../auth/useAccessToken'
import { Dialog } from '../Dialog'
import { canOwnArtifact } from '../../lib/tiers'
import { pushToast } from '../../stores/toastStore'

interface TransferOwnershipDialogProps {
  open: boolean
  onClose: () => void
  projectId: string
  /** Todos los members internos del proyecto. El diálogo filtra los
   * candidatos (role lead) internamente para mantener una sola fuente
   * de verdad — el caller no necesita pre-filtrar. */
  internalMembers: ProjectMember[]
  /** Catálogo de role templates (system + customs del tenant). Usado
   * para detectar `is_project_lead=true` en el role del member. */
  roles: RoleOption[]
  /** Lead actual (puede ser `null` si el proyecto no tenía lead). El
   * dialog lo muestra como hint y lo excluye del selector. */
  currentLeadMemberId: string | null
}

const MAX_REASON_LENGTH = 500

export function TransferOwnershipDialog({
  open,
  onClose,
  projectId,
  internalMembers,
  roles,
  currentLeadMemberId,
}: TransferOwnershipDialogProps) {
  const { t } = useTranslation(['projects', 'errors'])
  const { accessToken } = useAccessToken()
  const queryClient = useQueryClient()

  // Estado del formulario. Usamos `open` como key (via prop forwarding
  // al wrapper) en lugar de un useEffect de reset — patrón canónico de
  // React 19 que evita "Calling setState synchronously within an effect".
  const [newLeadId, setNewLeadId] = useState<string>('')
  const [reason, setReason] = useState<string>('')
  const [error, setError] = useState<string | null>(null)
  // Track de `open` para resetear el formulario cuando se cierra y se
  // vuelve a abrir. Si el componente padre lo cierra y reabre con
  // mismos props, el state se limpia (vs. mostrar el reason previo).
  const [lastOpenTick, setLastOpenTick] = useState(false)
  if (open !== lastOpenTick) {
    setLastOpenTick(open)
    if (open) {
      setNewLeadId('')
      setReason('')
      setError(null)
    }
  }

  // Candidatos: miembros con tier capaz de producir contenido (D8), que
  // es lo que el backend valida. Antes se filtraba por el flag
  // `is_project_lead` del rol en el catálogo — un rol no lidera nada, lo
  // hace una persona. Excluye al lead actual: transferirse a sí mismo es
  // un no-op que solo confunde.
  const candidates = useMemo(() => {
    return internalMembers.filter(
      (m) => m.id !== currentLeadMemberId && canOwnArtifact(m.user_tier),
    )
  }, [internalMembers, currentLeadMemberId])

  const currentLeadName = useMemo(() => {
    if (!currentLeadMemberId) return null
    const m = internalMembers.find((x) => x.id === currentLeadMemberId)
    if (!m) return null
    return m.user_display_name ?? m.user_email ?? m.external_email ?? '—'
  }, [internalMembers, currentLeadMemberId])

  const mutation = useMutation({
    mutationFn: async () => {
      if (!accessToken) throw new Error('no_access_token')
      if (!newLeadId) {
        setError(t('team.transfer_dialog.new_lead_placeholder'))
        throw new Error('validation')
      }
      const trimmed = reason.trim()
      return transferOwnership(accessToken, projectId, {
        new_lead_member_id: newLeadId,
        reason: trimmed ? trimmed : null,
      })
    },
    onSuccess: () => {
      const target = candidates.find((m) => m.id === newLeadId)
      const name =
        target?.user_display_name ??
        target?.user_email ??
        target?.external_email ??
        '—'
      pushToast({
        kind: 'success',
        message: t('team.transfer_dialog.toast_success', { name }),
      })
      // Invalida proyecto (lead_member_id cambió) + audit (si la UI lo
      // consume) + members (raci_role no cambia pero la UI pinta badge
      // distinto según lead_member_id).
      void queryClient.invalidateQueries({
        queryKey: ['projects', projectId],
      })
      void queryClient.invalidateQueries({
        queryKey: ['projects', projectId, 'members'],
      })
      onClose()
    },
    onError: (err) => {
      if (err instanceof Error && err.message === 'validation') return
      const code = err instanceof ApiError ? err.code : null
      const fallback = t('team.transfer_dialog.toast_failed')
      const message = code
        ? t(`errors:${code}` as 'errors:UNKNOWN', { defaultValue: fallback })
        : fallback
      setError(message)
    },
  })

  const submitting = mutation.isPending
  const disabled = submitting || !newLeadId || candidates.length === 0

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!submitting) onClose()
      }}
      title={t('team.transfer_dialog.title')}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-sm text-ink-muted hover:text-ink rounded-md disabled:opacity-50"
          >
            {t('team.transfer_dialog.cancel')}
          </button>
          <button
            type="button"
            onClick={() => mutation.mutate()}
            disabled={disabled}
            className="px-5 py-2 bg-brand hover:bg-brand-deep text-canvas text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting
              ? t('team.transfer_dialog.submitting')
              : t('team.transfer_dialog.submit')}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="font-body text-sm text-ink-muted">
          {t('team.transfer_dialog.subtitle')}
        </p>

        <p className="font-mono text-xs text-ink-subtle">
          {currentLeadName
            ? t('team.transfer_dialog.current_lead_hint', {
                name: currentLeadName,
              })
            : t('team.transfer_dialog.no_current_lead_hint')}
        </p>

        <label className="block">
          <span className="text-xs font-medium text-ink-muted">
            {t('team.transfer_dialog.new_lead_label')}
          </span>
          {candidates.length === 0 ? (
            <p className="mt-2 font-body text-sm text-ink-muted italic">
              {t('team.transfer_dialog.no_candidates')}
            </p>
          ) : (
            <select
              value={newLeadId}
              onChange={(e) => setNewLeadId(e.target.value)}
              disabled={submitting}
              className="mt-1 w-full bg-canvas border border-line rounded-md px-3 py-2 text-sm focus:outline-none focus:border-brand"
            >
              <option value="">
                {t('team.transfer_dialog.new_lead_placeholder')}
              </option>
              {candidates.map((m) => {
                const display =
                  m.user_display_name ??
                  m.user_email ??
                  m.external_email ??
                  '—'
                const roleNames = m.roles.map((key) => roleLabel(key, roles)).join(', ')
                return (
                  <option key={m.id} value={m.id}>
                    {display} · {roleNames}
                  </option>
                )
              })}
            </select>
          )}
        </label>

        <label className="block">
          <span className="text-xs font-medium text-ink-muted">
            {t('team.transfer_dialog.reason_label')}
          </span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value.slice(0, MAX_REASON_LENGTH))}
            disabled={submitting}
            rows={3}
            maxLength={MAX_REASON_LENGTH}
            placeholder={t('team.transfer_dialog.reason_placeholder')}
            className="mt-1 w-full bg-canvas border border-line rounded-md px-3 py-2 text-sm focus:outline-none focus:border-brand resize-none"
          />
          <span className="mt-1 block text-[11px] text-ink-subtle">
            {t('team.transfer_dialog.reason_help')}
          </span>
        </label>

        {error ? (
          <div className="rounded-md border border-critical/40 bg-critical-soft p-2.5 font-mono text-xs text-critical">
            {error}
          </div>
        ) : null}
      </div>
    </Dialog>
  )
}
